import {
  type DocumentTableRow,
  formatDocumentDisplayTitle,
} from 'features/knowledgeDocumentViewer'
import { type Highlight } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useResolvedDocumentPageImage } from 'features/knowledgeDocumentViewer/hooks/useResolvedDocumentPageImage'
import { useGeckoPermissions } from 'features/permissions'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { Badge } from '@/src/designSystem/components/Badge'
import { Button } from '@/src/designSystem/components/Button'
import {
  Field,
  FieldDescription,
  FieldTitle,
} from '@/src/designSystem/components/Field'

import { DocumentPreviewMeta } from './DocumentPreviewMeta'
import { DocumentPreviewViewer } from './DocumentPreviewViewer'
import { PageHighlightGroup } from './PageHighlightGroup'
import { RelevanceScoreBar } from './RelevanceScoreBar'

const ContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const ViewerContainer = styled.div<{ $heightPercent: number }>`
  flex-shrink: 0;
  overflow: hidden;
  height: ${(props) => props.$heightPercent}%;
  min-height: 5rem;
`

const Resizer = styled.div`
  height: 0.375rem;
  flex-shrink: 0;
  cursor: row-resize;
  background-color: var(--border);
  transition: background-color 150ms;

  &:hover,
  &:active {
    background-color: var(--primary);
  }
`

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 1rem;
  overflow-y: auto;
  padding: 0.75rem 1rem;
`

const KeywordsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const HighlightsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const OpenButtonWrapper = styled.div`
  flex-shrink: 0;
  padding: 1rem;
`

const BlueLinkButton = styled(Button)`
  color: #60a5fa;
`

const FullWidthButton = styled(Button)`
  width: 100%;
`

const STORAGE_KEY = 'documentSearch_previewViewerHeight:v2'
const DEFAULT_VIEWER_HEIGHT_PERCENT = 55
const MIN_VIEWER_HEIGHT_PERCENT = 15
const MAX_VIEWER_HEIGHT_PERCENT = 75
const SUMMARY_TRUNCATE_LENGTH = 200

function readStoredViewerHeight(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = Number(stored)
      if (
        !Number.isNaN(parsed) &&
        parsed >= MIN_VIEWER_HEIGHT_PERCENT &&
        parsed <= MAX_VIEWER_HEIGHT_PERCENT
      )
        return parsed
    }
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_VIEWER_HEIGHT_PERCENT
}

export interface DocumentPreviewContentProps {
  document: DocumentTableRow
  highlights?: Highlight[] | null
  showSummary?: boolean
  showKeywords?: boolean
  showHighlights?: boolean
  relevanceScore?: number
  detailScores?: Record<string, number>
  showIndividualScores?: boolean
  showRelevanceBar?: boolean
  onOpen?: (doc: DocumentTableRow) => void
  onFragmentClick?: (pageNum: number, fragmentHtml: string) => void
}

/**
 * Shared preview body: thumbnail/viewer, metadata, optional summary/keywords/highlights,
 * optional relevance bar, optional open button. Used by both DocumentPreview (card view)
 * and DocumentPreviewPanel (table side panel).
 */
export const DocumentPreviewContent: React.FC<DocumentPreviewContentProps> = ({
  document,
  highlights = null,
  showSummary = true,
  showKeywords = true,
  showHighlights = true,
  relevanceScore,
  detailScores,
  showIndividualScores = true,
  showRelevanceBar = true,
  onOpen,
  onFragmentClick,
}) => {
  const { hasPermissions } = useGeckoPermissions()
  const isGeckoUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const { siteID } = useKnowledgeDocumentDataContext()
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1)
  const totalPages = document.numPages ?? 1

  useEffect(() => {
    setCurrentPreviewPage(1)
  }, [document.id])

  const { thumbnailUrl, isThumbLoading, hiResUrl } =
    useResolvedDocumentPageImage(document.id, siteID, {
      pageNumber: currentPreviewPage,
      skipHiRes: false,
      knownHosts: {
        systemDocumentEntryId: document.id,
        filename: document.filename,
        bqDocumentId: document.bqDocumentId,
        cantileverDocumentId: document.cantileverDocumentId,
        hasHydratedDocumentHosts: true,
        // Preview can UUID5-derive page hosts; do not wait on Rolodex page lookup.
        skipPageHostLookup: true,
      },
    })

  const [viewerHeightPercent, setViewerHeightPercent] = useState(
    readStoredViewerHeight
  )
  const contentRootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(viewerHeightPercent))
    } catch {
      /* localStorage unavailable */
    }
  }, [viewerHeightPercent])

  const handleResizerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const container = contentRootRef.current
      if (!container) return
      const startY = e.clientY
      const startHeight = viewerHeightPercent

      const onMouseMove = (moveEvent: MouseEvent) => {
        const containerRect = container.getBoundingClientRect()
        const dy = moveEvent.clientY - startY
        const pctDelta = (dy / containerRect.height) * 100
        const next = Math.min(
          Math.max(startHeight + pctDelta, MIN_VIEWER_HEIGHT_PERCENT),
          MAX_VIEWER_HEIGHT_PERCENT
        )
        setViewerHeightPercent(next)
      }

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }

      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    },
    [viewerHeightPercent]
  )

  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
  const summary = document.summary ?? ''
  const isTruncated = summary.length > SUMMARY_TRUNCATE_LENGTH
  const displayedSummary =
    isTruncated && !isSummaryExpanded
      ? `${summary.slice(0, SUMMARY_TRUNCATE_LENGTH)}…`
      : summary

  const displayTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )

  return (
    <ContentRoot ref={contentRootRef}>
      <ViewerContainer $heightPercent={viewerHeightPercent}>
        <DocumentPreviewViewer
          thumbnailUrl={thumbnailUrl ?? null}
          hiResUrl={hiResUrl ?? null}
          isThumbLoading={isThumbLoading}
          alt={displayTitle}
          documentId={document.id}
          currentPage={currentPreviewPage}
          totalPages={totalPages}
          onPageChange={setCurrentPreviewPage}
        />
      </ViewerContainer>

      <Resizer
        onMouseDown={handleResizerMouseDown}
        role='separator'
        aria-orientation='horizontal'
      />

      <ScrollableContent>
        {showRelevanceBar && isGeckoUser && (
          <RelevanceScoreBar
            relevance={relevanceScore}
            detailScores={detailScores}
            showIndividualScores={showIndividualScores}
          />
        )}

        <DocumentPreviewMeta document={document} />

        {showSummary && summary && (
          <Field orientation='vertical'>
            <FieldTitle>Summary</FieldTitle>
            <FieldDescription>{displayedSummary}</FieldDescription>
            {isTruncated && (
              <BlueLinkButton
                type='button'
                variant='link'
                size='inline'
                onClick={() => setIsSummaryExpanded((prev) => !prev)}>
                {isSummaryExpanded ? 'Show less' : 'Show more'}
              </BlueLinkButton>
            )}
          </Field>
        )}

        {showKeywords && document.keywords.length > 0 && (
          <Field>
            <FieldTitle>Keywords</FieldTitle>
            <KeywordsWrapper>
              {document.keywords.map((keyword) => (
                <Badge key={keyword} variant='outline'>
                  {keyword}
                </Badge>
              ))}
            </KeywordsWrapper>
          </Field>
        )}

        {showHighlights && highlights && highlights.length > 0 && (
          <Field>
            <FieldTitle>Matched Text</FieldTitle>
            <HighlightsWrapper>
              {highlights.map((highlight) => (
                <PageHighlightGroup
                  key={highlight.page_num}
                  highlight={highlight}
                  onFragmentClick={onFragmentClick}
                />
              ))}
            </HighlightsWrapper>
          </Field>
        )}
      </ScrollableContent>

      {onOpen && (
        <OpenButtonWrapper>
          <FullWidthButton
            variant='primaryRaised'
            size='lg'
            onClick={() => onOpen(document)}>
            Open Document
          </FullWidthButton>
        </OpenButtonWrapper>
      )}
    </ContentRoot>
  )
}
